import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { TermsAcceptanceLabel } from "@/lib/terms-acceptance";
import { Loader2 } from "lucide-react";

export default function AcceptTerms() {
  const { user, loading, updateTermsAcceptance } = useAuth();
  const navigate = useNavigate();
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-wt-bg-primary text-wt-text-primary p-4">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="font-sans text-wt-text-muted">Loading…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: { pathname: "/accept-terms" } }} replace />;
  }

  if (user.user_metadata?.terms_accepted === true) {
    return <Navigate to="/app" replace />;
  }

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreeToTerms) {
      setError("You must agree to the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setError(null);
    setSubmitting(true);
    const { error: err } = await updateTermsAcceptance();
    setSubmitting(false);
    if (err) {
      setError(err.message);
      return;
    }
    navigate("/app", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-wt-bg-primary text-wt-text-primary p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center mb-4">
            <Logo variant="fullTransparent" size="h-16" />
          </Link>
          <h1 className="font-sans text-2xl font-bold text-wt-text-primary">Watt Tower</h1>
          <p className="font-sans text-wt-text-secondary mt-1 text-sm">
            Accept terms to continue
          </p>
        </div>

        <div className="rounded-lg border border-wt-border-default bg-wt-bg-secondary p-6">
          <form onSubmit={handleConfirm} className="space-y-4">
            <div className="flex items-start gap-3 rounded-md border border-wt-border-default bg-wt-bg-tertiary p-4">
              <Checkbox
                id="acceptTerms"
                checked={agreeToTerms}
                onCheckedChange={(v) => setAgreeToTerms(v === true)}
                className="mt-0.5 border-wt-slate-blue data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <Label
                htmlFor="acceptTerms"
                className="font-sans text-sm font-normal leading-relaxed text-wt-text-secondary cursor-pointer"
              >
                <TermsAcceptanceLabel linkClassName="text-primary hover:underline" />
              </Label>
            </div>

            {error && (
              <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm font-sans">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11 bg-primary font-sans font-semibold text-wt-bg-primary hover:bg-wt-green-muted"
              disabled={submitting || !agreeToTerms}
            >
              {submitting ? "Please wait…" : "Continue to app"}
            </Button>
          </form>
        </div>

        <p className="font-sans text-xs text-wt-text-muted text-center mt-6">
          <Link to="/" className="text-wt-text-secondary hover:text-wt-text-primary">
            ← Back to home
          </Link>
        </p>
      </div>
    </div>
  );
}
